import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Badge, Button, EmptyState, ErrorState, Input, Skeleton, cn } from '@vta/ui';
import { programSchema, type Program, type TeamSummary } from '@vta/types';
import { apiClient } from '../lib/api-client.ts';
import { OgMeta } from '../lib/og-meta.tsx';
import { FiltersBar, type FilterValues } from '../features/filters/FiltersBar.tsx';
import { formatProgramLabel, formatTeamNumber, programBadgeVariant } from '../lib/format.ts';

const PAGE_SIZE = 50;
const VIRTUALIZATION_THRESHOLD = 100;

/**
 * Advanced explore page. URL state mirrors the filter values so a query is
 * shareable. Search params are validated with the same shape as the root
 * route filters, plus an optional `q` text query for free-text filtering.
 */
const exploreSearchSchema = z.object({
  q: z.string().min(1).max(100).optional(),
  program: programSchema.optional(),
  country: z.string().min(1).max(120).optional(),
  region: z.string().min(1).max(120).optional(),
  division: z.string().min(1).max(120).optional(),
  event: z.string().min(1).max(120).optional(),
  hasStory: z.boolean().optional(),
});

type ExploreSearch = z.infer<typeof exploreSearchSchema>;

export const Route = createFileRoute('/explore')({
  validateSearch: exploreSearchSchema,
  component: ExploreRoute,
});

interface TeamsResponse {
  teams: TeamSummary[];
}

interface CountriesResponse {
  countries: Array<{ countrySlug: string; country: string; teamCount: number; cityCount: number }>;
}

function ExploreRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [shown, setShown] = useState(PAGE_SIZE);

  const filterValues: FilterValues = {
    program: search.program,
    country: search.country,
    region: search.region,
    division: search.division,
    event: search.event,
    hasStory: search.hasStory,
  };

  const onChangeFilters = (next: Partial<FilterValues>) => {
    navigate({
      search: (prev: ExploreSearch) => ({
        ...prev,
        ...next,
        // Prune empty/undefined explicitly so we don't write `?program=`
        program: next.program ?? prev.program,
        country: 'country' in next ? next.country : prev.country,
        region: 'region' in next ? next.region : prev.region,
        division: 'division' in next ? next.division : prev.division,
        event: 'event' in next ? next.event : prev.event,
        hasStory: 'hasStory' in next ? next.hasStory : prev.hasStory,
      }),
    });
  };

  const onChangeQuery = (q: string) => {
    navigate({
      search: (prev: ExploreSearch) => ({ ...prev, q: q.trim() === '' ? undefined : q.trim() }),
    });
  };

  const teams = useQuery<TeamsResponse>({
    queryKey: ['explore-teams', search.program, search.country, search.region, search.hasStory],
    queryFn: ({ signal }) =>
      apiClient.get<TeamsResponse>('/api/teams', {
        query: {
          program: search.program,
          country: search.country,
          region: search.region,
          hasStory: search.hasStory ? 'true' : undefined,
          limit: 100,
        },
        signal,
      }),
  });

  const countries = useQuery<CountriesResponse>({
    queryKey: ['countries'],
    queryFn: ({ signal }) => apiClient.get<CountriesResponse>('/api/countries', { signal }),
    staleTime: 10 * 60 * 1000,
  });

  const filtered = useMemo(() => {
    const list = teams.data?.teams ?? [];
    const q = search.q?.toLowerCase().trim();
    if (!q) return list;
    return list.filter((t) => {
      if (t.teamNumber.toLowerCase().includes(q)) return true;
      if (t.teamName.toLowerCase().includes(q)) return true;
      if (t.organization?.toLowerCase().includes(q)) return true;
      if (t.city?.toLowerCase().includes(q)) return true;
      if (t.country?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [teams.data, search.q]);

  const needsPaging = filtered.length > VIRTUALIZATION_THRESHOLD;
  const visible = needsPaging ? filtered.slice(0, shown) : filtered;

  return (
    <>
      <OgMeta
        title="Explore"
        description="Search and filter every catalogued VEX team."
        pathname="/explore"
      />
      <main className="container mx-auto flex flex-col gap-6 py-8 md:py-12">
        <header className="flex flex-col gap-3 border-b border-border-subtle pb-6">
          <p className="text-2xs uppercase tracking-[0.3em] text-fg-subtle">Explore</p>
          <h1 className="font-display text-3xl text-fg sm:text-4xl">All teams</h1>
          <p className="text-sm text-fg-muted">
            Search by team number, name, organization, or city. Combine with filters below.
          </p>
        </header>

        <Input
          value={search.q ?? ''}
          onChange={(e) => onChangeQuery(e.target.value)}
          placeholder="Search 839A, Toronto, Lake View Robotics…"
          aria-label="Filter teams"
        />

        <FiltersBar
          values={filterValues}
          onChange={onChangeFilters}
          countries={countries.data?.countries ?? []}
        />

        {teams.isLoading ? (
          <ListSkeleton />
        ) : teams.isError ? (
          <ErrorState title="Could not load teams" retry={() => teams.refetch()} />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No teams match"
            description="Try removing a filter or broadening your query."
          />
        ) : (
          <>
            <p className="text-2xs uppercase tracking-[0.18em] text-fg-subtle">
              {filtered.length} team{filtered.length === 1 ? '' : 's'} match
              {teams.data && teams.data.teams.length === 100 ? ' (showing first 100 from API)' : ''}
            </p>
            <ResultList teams={visible} />
            {needsPaging && shown < filtered.length ? (
              <div className="flex items-center justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShown((s) => Math.min(filtered.length, s + PAGE_SIZE))}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </main>
    </>
  );
}

function ResultList({ teams }: { teams: TeamSummary[] }) {
  return (
    <ul className="divide-y divide-border-subtle border-y border-border-subtle">
      {teams.map((t) => (
        <li key={t.teamNumber} className="flex items-center justify-between gap-3 py-3">
          <Link
            to="/team/$teamNumber"
            params={{ teamNumber: t.teamNumber }}
            className="flex min-w-0 flex-col"
          >
            <span className="font-mono text-sm text-fg">{formatTeamNumber(t.teamNumber)}</span>
            <span className="truncate text-xs text-fg-muted">{t.teamName}</span>
            {[t.city, t.country].filter(Boolean).length > 0 ? (
              <span className="truncate text-2xs text-fg-subtle">
                {[t.city, t.country].filter(Boolean).join(', ')}
              </span>
            ) : null}
          </Link>
          <div className="flex items-center gap-2">
            <Badge variant={programBadgeVariant(t.program)}>
              {formatProgramLabel(t.program, 'short')}
            </Badge>
            {t.hasStory ? <Badge variant="accent">Story</Badge> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ListSkeleton() {
  return (
    <div className={cn('flex flex-col gap-2')}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

// Re-export for type completeness (TanStack expects route-defined types).
export type { Program };
